<template>
  <div>
    <integration-tab-wrapper heading="smrtPhone Integration" logo-path="/images/smrtphone-icon.png" :is-onboarding-integration="isOnboardingIntegration">
      <template v-if="!isOnboardingIntegration" v-slot:buttons>
        <button-link
          class="mr-100"
          @click="$refs.changeAPIKeyModal.open()"
        >
          Change smrtPhone API Key
        </button-link>

        <button-link
          v-if="isSmrtPhoneIntegrationComplete"
          class="is-danger"
          @click="deleteIntegration"
          :loading="isDisconnecting"
          :disabled="isDisconnecting"
        >
          Disconnect smrtPhone
        </button-link>
      </template>

      <section-page is-short>
        <h2 class="step--heading">smrtPhone</h2>

        <div class="flex step mt-100">
          <p class="step--label">Step 1</p>

          <div>
            <p class="step--description"><a
              href="https://www.smrtphone.io/forefront"
              target="_blank">Sign up for smrtPhone</a> and select Forefront as your CRM
            </p>

            <p class="step--note--heading">IMPORTANT NOTES:</p>
            <p class="step--note--item">Sign up for your smrtPhone monthly plan using <strong>FOREFRONT30</strong> to get:</p>
            <p class="step--note--item pl-5">- 30% off for 3 months of smrtPhone Standard or Pro monthly subscription</p>
            <p class="step--note--item pl-5">- 30% off for 3 months of smrtDialer single or multi-line monthly subscription</p>
            <p class="step--note--item pl-5">- $2 off each phone number</p>

            <p class="step--note--item">Existing smrtPhone customers can switch their CRM to Forefront by visiting <a href="https://phone.smrt.studio/setup/connectCrm" target="_blank">this page</a>.</p>
          </div>
        </div>

        <div class="flex step mt-100">
          <p class="step--label">Step 2</p>

          <p class="step--description">When asked in smrtPhone enter this api key:
            <span class="mx-50 has-text-weight-bold">{{ apiKey }}</span>
            <a class="is-size-7" @click="copyApiKey">copy</a>
          </p>
        </div>

        <div class="flex step mt-100">
          <p class="step--label">Step 3</p>

          <p class="step--description">Make sure you click Activate Your Subscription in
            smrtPhone to make sure you get all the smrtPhone features</p>
        </div>

        <div class="flex step mt-100">
          <p class="step--label">Step 4</p>

          <p class="step--description">When you purchase a number be sure to setup the Call and Text Flow. <a
            href="https://docs.smrtphone.io/en/articles/5577818-receiving-incoming-text-messages-sms-inbox-applet-sms-flow"
            target="_blank">smrtPhone Docs</a></p>
        </div>

        <div class="flex step mt-100">
          <p class="step--label">Step 5</p>

          <p class="step--description">Make sure you install the smrtPhone Chrome extension and only use Chrome
            while using smrtPhone.</p>
        </div>

        <div class="flex step mt-100">
          <p class="step--label">Step 6</p>

          <p class="step--description">When you're done in smrtPhone come back here and tie your users together and
            manage how your smrtPhone numbers will work in Forefront.</p>
        </div>

        <app-loading-section :loading="isLoading">
          <div v-if="isSmrtPhoneIntegrationComplete" class="mt-200">
            <h2 class="user_heading">smrtPhone Users</h2>
            <p class="user_description">Select a Forefront users for each of your smrtPhone users so that we can know
              who's making and receiving communications.</p>

            <div class="user_table--wrapper">
              <table class="user_table">
                <tr>
                  <th class="user_table--heading">smrtPhone User</th>
                  <th class="user_table--heading">Forefront User</th>
                </tr>

                <tr
                  v-for="(smrtPhoneUser,index) in smrtPhoneUsers"
                  :key="index"
                >
                  <td class="user_table--label">{{ smrtPhoneUser.label }}</td>
                  <td class="user_table--select">
                    <form-field-select
                      v-model="smrtPhoneUser.user_id"
                    >
                      <option
                        v-for="forefrontUser in forefrontUsers"
                        :key="forefrontUser.value"
                        :value="forefrontUser.value"
                        :disabled="forefrontUser.isSelected"
                      >{{ forefrontUser.label }}
                      </option>
                    </form-field-select>
                  </td>
                </tr>
              </table>

              <div class="flex mt-50">
                <button-outlined
                  v-if="smrtPhoneUsers.length"
                  class="mr-100"
                  :loading="isSavingUsers"
                  @click="saveUserMapping"
                >
                  Save Users
                </button-outlined>

                <button-link
                  @click="syncUsers"
                >Sync Users with smrtPhone
                </button-link>
              </div>
            </div>
          </div>
        </app-loading-section>
      </section-page>
    </integration-tab-wrapper>

    <phone-numbers-settings
      v-if="isSmrtPhoneIntegrationComplete"
      ref="phoneNumberSettings"
      title="smrtPhone Numbers"
      :type="smrtPhoneType"
    />

    <modal-container>
      <modal-default
        ref="changeAPIKeyModal"
        title="Change smrtPhone API Key"
        is-double-column
      >
        <p class="current_api_key">Current API Key:
            <span v-if="isSmrtPhoneIntegrationComplete">{{ smrtPhoneIntegration.token.auth_token }}</span>
            <span v-else>Not Yet Setup</span>
        </p>

        <form-field-input
          label="New smrtPhone API Key"
          v-model="formData.token"
          :validation-errors="validationErrors.token"
        />

        <div>
          <button-default
            :async-click="changeAPIKey"
          >Save API Key
          </button-default>
        </div>
      </modal-default>
    </modal-container>
  </div>
</template>

<script>
import IntegrationBackButton from "../components/IntegrationBackButton.vue";
import PhoneNumbersSettings from "../components/PhoneNumbers.vue";
import {ENUMS} from "../../../services/enums";
import utils from "../../../services/utils";
import api from "../../../api";
import IntegrationTabWrapper from "../components/IntegrationTabWrapper.vue";

export default {
  name: "IntegrationSmrtPhone",
  components: {IntegrationTabWrapper, IntegrationBackButton, PhoneNumbersSettings},
  props: {
    isOnboardingIntegration: {
      type: Boolean,
      default: false
    },
  },
  data() {
    return {
      smrtPhoneUsers: {},
      isLoading: false,
      isDisconnecting: false,
      isSavingUsers: false,
      formData: {
        token: null,
      },
      validationErrors: {},
    }
  },
  mounted() {
    if (!this.hasSmrtPhoneIntegration) {
      this.setupIntegration();
    } else {
      this.getSmrtPhoneUsers();
    }

  },
  watch: {
    isSmrtPhoneIntegrationComplete(newValue, oldValue) {
      if (newValue) this.getSmrtPhoneUsers()
    }
  },
  computed: {
    smrtPhoneType() {
      return ENUMS.IntegrationTypeEnum.SmrtPhone.value;
    },
    market() {
      return this.$store.getters.market;
    },
    hasSmrtPhoneIntegration() {
      return this.$store.getters.hasSmrtPhoneIntegration;
    },
    isSmrtPhoneIntegrationComplete() {
      return this.$store.getters.isSmrtPhoneIntegrationComplete;
    },
    smrtPhoneIntegration() {
      return this.$store.getters.smrtPhoneIntegration;
    },
    forefrontUsers() {
      let users = this.$store.getters.activeUsers.map(user => {
        const hasSmrtPhoneUsers = Object.keys(this.smrtPhoneUsers).length > 0;
        let isSelected = false;

        if(hasSmrtPhoneUsers) {
          isSelected = !!(this.smrtPhoneUsers.find((smrtPhoneUser) => smrtPhoneUser.user_id === user.id))
        }

        return {
          value: user.id,
          label: user.full_name,
          isSelected: isSelected,
        }
      })

      users.unshift({
        value: null,
        label: 'None',
        isSelected: false
      })

      return users;
    },
    apiKey() {
      return this.smrtPhoneIntegration && this.smrtPhoneIntegration.data.api_key;
    }
  },
  methods: {
    syncUsers() {
      this.isLoading = true;

      return api.smrtPhone.syncSmrtPhoneUsers().then((res) => {
        this.smrtPhoneUsers = res.data
        this.isLoading = false;
      }).catch(() => this.isLoading = false)
    },
    changeAPIKey() {
      return api.smrtPhone.updateSmrtPhoneAPIKey(this.formData, this.validationErrors).then(() => {
        this.$refs.changeAPIKeyModal.close()
      });
    },
    setupIntegration() {
      this.isLoading = true;

      api.smrtPhone.create({market_id: this.market.id})
        .then(() => this.isLoading = false)
        .catch(() => this.isLoading = false);
    },
    getSmrtPhoneUsers() {
      this.isLoading = true;

      return api.smrtPhone.users().then((res) => {
        this.smrtPhoneUsers = res.data;
        this.isLoading = false;
      }).catch(() => {
        this.isLoading = false
      })
    },
    deleteIntegration() {
      this.isDisconnecting = true;

      api.smrtPhone.delete(this.smrtPhoneIntegration.id).then(() => {
        this.isDisconnecting = false;
        this.$router.push({name: "settings", params: {'tab': 'integrations'}})
      });
    },
    copyApiKey() {
      utils.copyToClipboard(this.apiKey);
    },
    saveUserMapping() {
      let payload = {users: []}

      payload.users = this.smrtPhoneUsers.map((smrtPhoneUser) => {
        return {
          id: smrtPhoneUser.id,
          forefront_user_id: smrtPhoneUser.user_id
        };
      });

      this.isSavingUsers = true
      api.smrtPhone.linkSmrtPhoneUsers(payload)
        .then(() => {
          this.$buefy.toast.open({
            message: "Users was linked successfully",
            type: "is-success"
          });
        })
        .finally(() => this.isSavingUsers = false)
    }
  },
}
</script>

<style lang="scss" scoped>
@import "../../../styles/variables/variables";

.api_heading {
  margin-bottom: 4px;
  font-size: 1.25rem;
  font-weight: bold;
}

.api_description {
  font-size: 0.875rem;
  color: $text-muted;
}

.step {
  margin-bottom: 20px;

  &--heading {
    margin-bottom: 4px;
    font-size: 1.25rem;
    font-weight: bold;
  }

  &--note {

    &--heading {
      margin: 12px 0 4px;
      text-transform: uppercase;
      font-size: 0.75rem;
      font-weight: bold;
      color: $info;
    }

    &--item {
      margin-bottom: 6px;
      padding-left: 8px;
      color: $info;
      font-size: 0.875rem;
      line-height: 1.3;

      strong {
        color: $info
      }

      a {
        color: $primary;
        text-decoration: underline;

        &:hover {
          color: $secondary;
        }
      }
    }
  }

  &--label {
    flex-basis: 90px;
    flex-shrink: 0;
    padding-top: 2px;
    font-size: 1.125rem;
    color: $text-light;
    text-transform: uppercase;
  }

  &--description {
    line-height: 1.3;

    a {
      text-decoration: underline;
    }
  }
}

.user_heading {
  margin-bottom: 4px;
  font-size: 1.25rem;
  font-weight: bold;
}

.user_description {
  font-size: 0.875rem;
  color: $text-muted;
  line-height: 1.3;
}

.user_table {
  width: 100%;

  &--wrapper {
    width: 100%;
    max-width: 540px;
    margin: 20px 0 10px;
  }

  &--heading {
    padding-bottom: 12px;
    color: $text-muted;
    font-size: 0.875rem;
  }

  &--label {
    padding-bottom: 6px;
    vertical-align: middle;
  }

  &--select {
    padding-bottom: 6px;
  }
}

.current_api_key {
  margin: 1rem 0;
  padding-bottom: 1rem;
  border-bottom: 1px solid $border;

  span {
    font-weight: bold;
  }
}
</style>
